import React, { useEffect, useRef, useState } from "react";
import { SimpleClientPag } from "../../interfaces/simpleClient";
import { useLocation, useNavigate } from "react-router-dom";
import { getClientsWithParams } from "../../services/clientService";
import ToolTip from "../../components/component-tools/ToolTip";

const Clients = () => {
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [allClients, setAllClients] = useState<SimpleClientPag[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [clientStatusID, setClientStatusID] = useState<number>(0);
  const [appliedClientStatusID, setAppliedClientStatusID] = useState<number>(0);
  const [showFilter, setShowFilter] = useState(false);

  const observer = useRef<IntersectionObserver | null>(null);
  const lastClientRef = useRef<HTMLTableRowElement | null>(null);

  type SortMode = "updated" | "name";
  const [sortMode, setSortMode] = useState<SortMode>("updated");
  const [sortDisabled, setSortDisabled] = useState(true);

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      const trimmedSearchTerm = searchTerm.trim();
      setDebouncedSearchTerm(trimmedSearchTerm);

      if (trimmedSearchTerm === "") {
        setPageNumber(1);
        setHasMore(true);
      } else {
        setAllClients([]);
        setPageNumber(1);
        setHasMore(true);
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        setSortDisabled(true);
        setLoading(true);

        const { clients, totalRecords } = await getClientsWithParams(
          pageNumber,
          100,
          debouncedSearchTerm,
          appliedClientStatusID,
          sortMode
        );

        setAllClients((prevClients) => {
          const existingIds = new Set(
            prevClients.map((client) => client.clientID)
          );
          const uniqueClients = clients.filter(
            (o) => !existingIds.has(o.clientID)
          );
          const combined = [...prevClients, ...uniqueClients];

          if (combined.length >= totalRecords) setHasMore(false);
          return combined;
        });
      } catch (error) {
        setErrorMessage("Failed to load clients.");
        console.error("Error fetching clients:", error);
      } finally {
        setTimeout(() => setSortDisabled(false), 1000);
        setLoading(false);
      }
    };

    fetchClients();
  }, [
    debouncedSearchTerm,
    pageNumber,
    appliedClientStatusID,
    clientStatusID,
    sortMode,
  ]);

  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.successMessage, location.pathname, navigate]);

  // Infinite scroll observer
  useEffect(() => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPageNumber((prev) => prev + 1);
        }
      },
      { threshold: 1.0 }
    );

    if (lastClientRef.current) observer.current.observe(lastClientRef.current);

    return () => observer.current?.disconnect();
  }, [hasMore, loading]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const applyFilter = () => {
    if (clientStatusID) {
      setAppliedClientStatusID(clientStatusID);
    }

    setAllClients([]);
    setPageNumber(1);
    setHasMore(true);
    setShowFilter(false);
  };

  const resetFilter = () => {
    setClientStatusID(0);
    setAppliedClientStatusID(0);
    setAllClients([]);
    setPageNumber(1);
    setHasMore(true);
    setShowFilter(false);
  };

  const resetAndApplySort = (newSort: SortMode) => {
    setAllClients([]);
    setPageNumber(1);
    setHasMore(true);
    setLoading(true);
    setSortMode(newSort);
  };

  return (
    <div className="content-container">
      <div className="header-container">
        <h1 style={{ display: "flex" }}>
          All Clients
          <ToolTip
            tooltipText="All clients, active or not."
            placement="right"
            className="info-icon"
          />
        </h1>
        <div className="page-items-container">
          <div className="sort-toggle">
            <button
              className={`btn-funnel ${sortDisabled ? "disabled" : ""}`}
              onClick={() =>
                resetAndApplySort(sortMode === "updated" ? "name" : "updated")
              }
              title={
                sortMode === "updated"
                  ? "Sorted by Last Updated"
                  : "Sorted by Name"
              }
              disabled={sortDisabled}
            >
              {sortMode === "updated" ? (
                <i className="bi bi-clock-history filter-icon"></i>
              ) : (
                <i className="bi bi-sort-alpha-down filter-icon"></i>
              )}
            </button>
          </div>
          <button
            className="btn-funnel"
            onClick={() => setShowFilter(!showFilter)}
          >
            <i
              className={`bi bi-funnel-fill filter-icon ${
                appliedClientStatusID ? "active-filter-icon" : ""
              }`}
            ></i>
          </button>

          <div className="search-container" style={{ width: "375px" }}>
            <input
              style={{ border: "none", width: "100%", outline: "none" }}
              type="text"
              placeholder="Search Client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm.trim() ? (
              <i
                className="bi bi-x-circle clear-icon"
                onClick={() => setSearchTerm("")}
                style={{ cursor: "pointer" }}
              ></i>
            ) : (
              <i className="bi bi-search search-icon"></i>
            )}
          </div>
          <button
            className="oxbtn btn-new"
            onClick={() => navigate(`/admin/create-client`)}
          >
            <div> New Client </div>
            <i className="bi bi-plus-circle-fill plus-icon"></i>
          </button>
        </div>
      </div>

      {/* Filter Popup */}
      {showFilter && (
        <>
          <div
            className="background-mask"
            onClick={() => setShowFilter(false)}
          ></div>
          <div className="filter-popup">
            <div className="close-button-container">
              <button
                className="close-button"
                onClick={() => setShowFilter(false)}
              >
                <i className="bi bi-x-circle"></i>
              </button>
            </div>
            <div className="filter-header">
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "5px",
                }}
              >
                <h2>Filter Clients</h2>
                <ToolTip
                  tooltipText="Pending - need admin review
    	                      Approved - admin approved
                            New - No orders placed under client"
                  placement="right"
                  className="info-icon-spec"
                />
              </div>
            </div>
            <div className="filter-body">
              <div className="field-stacked">
                <div
                  style={{
                    display: "flex",
                    gap: "20px",
                    justifyContent: "center",
                    paddingTop: "10px",
                  }}
                >
                  <div
                    className={`chip-date ${
                      clientStatusID === 1 ? "active-filter" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                    onClick={() => setClientStatusID(1)}
                  >
                    <div>{"Pending"}</div>
                  </div>
                  <div
                    className={`chip-date ${
                      clientStatusID === 2 ? "active-filter" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                    onClick={() => setClientStatusID(2)}
                  >
                    <div>{"Approved"}</div>
                  </div>
                  <div
                    className={`chip-date ${
                      clientStatusID === 3 ? "active-filter" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                    onClick={() => setClientStatusID(3)}
                  >
                    <div>{"New"}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="filter-actions">
              <button className="smbtn btn-grey" onClick={resetFilter}>
                Reset
              </button>
              <button
                className={`smbtn btn-green ${
                  clientStatusID === 0 ? "disabled" : ""
                }`}
                onClick={applyFilter}
                disabled={clientStatusID === 0}
              >
                Apply
              </button>
            </div>
          </div>
        </>
      )}

      {loading && pageNumber === 1 ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          <div className="table-wrapper">
            <div className="table-container" style={{ fontSize: "large" }}>
              <table className="table" style={{ cursor: "pointer" }}>
                <thead>
                  <tr>
                    <th style={{ width: "250px" }}>Client Name</th>
                    <th style={{ width: "100px" }}>Abbreviation</th>
                    <th style={{ width: "200px" }}>Client Name Merge Field</th>
                    <th style={{ width: "100px" }}>Denomination</th>
                    <th style={{ width: "100px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {allClients?.length === 0 ? (
                    <tr>
                      <td colSpan={5} style={{ textAlign: "center" }}>
                        No clients found.
                      </td>
                    </tr>
                  ) : (
                    allClients?.map((client, index) => (
                      <tr
                        key={client.clientID}
                        onClick={() =>
                          navigate(`/admin/view-client/${client.clientID}`)
                        }
                      >
                        <td>
                          <u>{client.clientName}</u>
                        </td>
                        <td>{client.abbreviation}</td>
                        <td>{client.diocese}</td>
                        <td>{client.denomination}</td>
                        <td>
                          {!client.isActive ? (
                            <div className="chip-date">
                              <div>Inactive</div>
                            </div>
                          ) : client.clientStatusID === 1 ? (
                            <div className="chip-date">
                              <div>Pending</div>
                            </div>
                          ) : client.clientStatusID === 3 ? (
                            <div className="chip-new">
                              <div>New</div>
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div ref={lastClientRef} style={{ height: "1px" }}></div>
            </div>
          </div>
        </>
      )}
      {errorMessage && (
        <div
          className="alert-message alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setErrorMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
    </div>
  );
};

export default Clients;
